const EXPORT_HISTORY_KEY = 'lottri_export_history_v1'

const quoteCsv = (value) => {
  const text = String(value ?? '')
  if (!text.includes(',') && !text.includes('"') && !text.includes('\n')) return text
  return `"${text.replaceAll('"', '""')}"`
}

export const toCsv = (rows, headers) => {
  const list = Array.isArray(rows) ? rows : []
  const columns = Array.isArray(headers) ? headers : []
  const lines = [columns.map((column) => quoteCsv(column.label)).join(',')]
  for (const row of list) {
    lines.push(columns.map((column) => quoteCsv(row?.[column.key])).join(','))
  }
  return lines.join('\n')
}

export const toLifecycleFilename = ({ scope, lifecycle, exportedAt = new Date() }) => {
  const date = new Date(exportedAt)
  const ts = Number.isNaN(date.getTime()) ? new Date() : date
  const compact = ts.toISOString().replaceAll(':', '-').replaceAll('.', '-')
  const normalizedScope = String(scope || 'report').trim().toLowerCase().replace(/\s+/g, '-')
  const normalizedLifecycle = String(lifecycle || 'operational').trim().toLowerCase().replace(/\s+/g, '-')
  return `${normalizedScope}-${normalizedLifecycle}-${compact}.csv`
}

export const downloadCsv = (filename, csvContent) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

const readExportHistory = () => {
  try {
    const raw = localStorage.getItem(EXPORT_HISTORY_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const writeExportHistory = (entries) => {
  localStorage.setItem(EXPORT_HISTORY_KEY, JSON.stringify(entries.slice(0, 25)))
}

export const recordExportEvent = (event) => {
  const current = readExportHistory()
  const normalized = {
    id: `${Date.now()}-${Math.random().toString(16).slice(2, 9)}`,
    exported_at: new Date().toISOString(),
    exported_by: event?.exported_by || 'unknown',
    export_scope: event?.export_scope || 'unknown',
    partial_export_warning: Boolean(event?.partial_export_warning),
    record_count: Number(event?.record_count ?? 0),
  }
  writeExportHistory([normalized, ...current])
  return normalized
}

export const getRecentExportHistory = () => readExportHistory()

export const toPaymentReconciliationRows = (payments) => {
  const list = Array.isArray(payments) ? payments : []
  return list.map((payment) => ({
    payment_id: payment?.id ?? '',
    reference: payment?.reference ?? '',
    status: payment?.status ?? '',
    amount: payment?.amount ?? 0,
    currency: payment?.currency ?? '',
    ticket_number: payment?.ticketNumber ?? '',
    degraded: payment?.degraded === true ? 'yes' : 'no',
    partial: payment?.partial === true ? 'yes' : 'no',
    updated_at: payment?.updatedAt ?? '',
  }))
}

export const toNotificationDeliveryRows = (notifications) => {
  const list = Array.isArray(notifications) ? notifications : []
  return list.map((notification) => ({
    notification_id: notification?.id ?? '',
    channel: notification?.channel ?? '',
    recipient: notification?.recipient ?? '',
    status: notification?.status ?? '',
    subject: notification?.subject ?? '',
    degraded: notification?.degraded === true ? 'yes' : 'no',
    partial: notification?.partial === true ? 'yes' : 'no',
    created_at: notification?.createdAt ?? '',
    updated_at: notification?.updatedAt ?? '',
  }))
}

export const toFailedOperationsRows = ({ payments = [], notifications = [] } = {}) => {
  return [
    ...toPaymentReconciliationRows(payments).filter((payment) => payment.status === 'failed' || payment.partial === 'yes' || payment.degraded === 'yes')
      .map((payment) => ({ type: 'payment', ...payment })),
    ...toNotificationDeliveryRows(notifications).filter((notification) => notification.status === 'failed' || notification.status === 'bounced' || notification.degraded === 'yes')
      .map((notification) => ({ type: 'notification', ...notification })),
  ]
}

export const toOrgFinancialSummaryRows = (rows) => {
  const list = Array.isArray(rows) ? rows : []
  return list.map((row) => ({
    organization_id: row?.orgId ?? row?.organization_id ?? '',
    lotteries: Number(row?.lotteries ?? 0),
    total_tickets: Number(row?.totalTickets ?? 0),
    sold_tickets: Number(row?.soldTickets ?? 0),
    reserved_tickets: Number(row?.reservedTickets ?? 0),
    refunded_tickets: Number(row?.refundedTickets ?? 0),
    voided_tickets: Number(row?.voidedTickets ?? 0),
    estimated_revenue: Number(row?.estimatedRevenue ?? 0),
    mismatch_count: Number(row?.mismatchCount ?? 0),
    partial: row?.partial === true ? 'yes' : 'no',
    degraded: row?.degraded === true ? 'yes' : 'no',
  }))
}

export const toSellerSettlementRows = (rows) => {
  const list = Array.isArray(rows) ? rows : []
  return list.map((row) => ({
    seller_id: row?.sellerId ?? row?.seller_id ?? '',
    seller_name: row?.sellerName ?? row?.seller_name ?? '',
    assigned_tickets: Number(row?.assignedTickets ?? 0),
    sold_tickets: Number(row?.soldTickets ?? 0),
    reserved_tickets: Number(row?.reservedTickets ?? 0),
    refunded_tickets: Number(row?.refundedTickets ?? 0),
    estimated_settlement: Number(row?.estimatedSettlement ?? 0),
    outstanding_reconciliation: row?.outstandingReconciliation === true ? 'yes' : 'no',
  }))
}

export const toLotteryReconciliationRows = (rows) => {
  const list = Array.isArray(rows) ? rows : []
  return list.map((row) => ({
    lottery_id: row?.lotteryId ?? row?.lottery_id ?? '',
    lottery_name: row?.name ?? row?.lottery_name ?? '',
    lifecycle: row?.lifecycle ?? '',
    total_tickets: Number(row?.totalTickets ?? 0),
    sold_tickets: Number(row?.soldTickets ?? 0),
    reserved_tickets: Number(row?.reservedTickets ?? 0),
    refunded_tickets: Number(row?.refundedTickets ?? 0),
    voided_tickets: Number(row?.voidedTickets ?? 0),
    sold_ratio: Number(row?.soldRatio ?? 0),
    reserved_ratio: Number(row?.reservedRatio ?? 0),
    estimated_revenue: Number(row?.estimatedRevenue ?? 0),
    mismatch_count: Number(row?.mismatchCount ?? 0),
    partial: row?.partial === true ? 'yes' : 'no',
    degraded: row?.degraded === true ? 'yes' : 'no',
  }))
}

